/**
 * ClientWorkspaceSidebar — vertical folder tree panel for the Client Workspace.
 *
 * Shows:
 * - Smart Views: "All Files", "Recently Added", "Email (disabled)"
 * - Client workspace folders with recursive children support
 * - Upload / New Folder action buttons (gated by RBAC)
 * - Inline rename / delete context menus per folder
 * - Expand/collapse per folder node
 * - Drag-drop target support on folders
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { ClientWorkspaceFolder } from "./types";

type SidebarAction = "rename" | "delete";

/* === Types === */

export interface ClientWorkspaceSidebarProps {
  clientId: number;
  /** Currently active folder id, or "all_files" for the root aggregation view. */
  activeFolderId: string;
  /** Currently active smart view id (for highlighting). */
  activeSmartView?: string;
  onSelectFolder: (folderId: string) => void;
  /** Callback when a smart view is selected. */
  onSelectSmartView?: (viewId: string) => void;
  /** True when the user may upload files. */
  canUpload: boolean;
  /** True when the user may create / rename / delete folders. */
  canManageFolders: boolean;
  /** Root-level folders (parent_id = NULL). */
  rootFolders: ClientWorkspaceFolder[];
  /** Callback to create a child folder (parent_id). */
  onCreateFolder: (parent_id: number | null, name: string) => Promise<ClientWorkspaceFolder>;
  /** Callback to rename a folder. */
  onRenameFolder: (folderId: number, name: string) => Promise<boolean>;
  /** Callback to delete a folder (without confirmation). */
  onDeleteFolder: (folderId: number) => Promise<boolean>;
  /** Callback when a file is dropped on a folder (drag-drop target). */
  onDropIntoFolder?: (e: React.DragEvent, folder: ClientWorkspaceFolder) => void;
  /** Callback to open upload modal. */
  onUploadClick?: () => void;
}

/* === Constants === */

const SIDEBAR_WIDTH = 270;
const ROW_HEIGHT = 38;

const SMART_VIEWS = [
  { id: "all_files", label: "All Files", icon: <StorageIcon sx={{ fontSize: 18 }} /> },
  { id: "recently_added", label: "Recently Added", icon: <ScheduleIcon sx={{ fontSize: 18 }} /> },
  { id: "email", label: "Email (disabled)", icon: <EmailOutlinedIcon sx={{ fontSize: 18 }} />, disabled: true },
];

/* === Helpers === */

function FolderIcon({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1,
        bgcolor: active ? "#B8D4F0" : "#E8F0FA",
        mr: 1,
      }}
    >
      <FolderOpenIcon sx={{ fontSize: 14, color: active ? "#0a3142" : "#1a73e8" }} />
    </Box>
  );
}

function FileCountChip({ count }: { count: number }) {
  return (
    <Box
      sx={{
        bgcolor: "#f0f0f0",
        borderRadius: 1,
        px: 0.75,
        py: 0.1,
        fontSize: 10,
        color: "#6b7280",
      }}
    >
      {count}
    </Box>
  );
}

/* === Smart View Row === */

interface SmartViewRowProps {
  view: { id: string; label: string; icon: React.ReactNode; disabled?: boolean };
  isActive: boolean;
  onSelect: () => void;
}

const SmartViewRow: React.FC<SmartViewRowProps> = ({ view, isActive, onSelect }) => {
  const row = (
    <ListItemButton
      dense
      onClick={onSelect}
      selected={isActive}
      disabled={view.disabled}
      sx={{
        minHeight: ROW_HEIGHT,
        height: ROW_HEIGHT,
        pl: 2,
        pr: 1,
        backgroundColor: isActive ? "#DBE8F5" : "transparent",
        "&:hover": { bgcolor: "#ECF1F8" },
        "&.Mui-selected": { bgcolor: "#DBE8F5" },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      <ListItemIcon sx={{ minWidth: 28, color: isActive ? "#0a3142" : "#496682" }}>
        {view.icon}
      </ListItemIcon>
      <ListItemText
        primary={view.label}
        primaryTypographyProps={{
          fontSize: 13,
          color: isActive ? "#0a3142" : "#496682",
          fontWeight: isActive ? 600 : 400,
          noWrap: true,
        }}
      />
    </ListItemButton>
  );

  if (view.disabled) {
    return (
      <Tooltip title="Coming soon" placement="right">
        <span>{row}</span>
      </Tooltip>
    );
  }

  return row;
};

/* === Folder row with inline rename / delete === */

interface FolderRowProps {
  folder: ClientWorkspaceFolder;
  depth: number;
  isActive: boolean;
  isExpanded: boolean;
  hasChildren?: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onRename: (name: string) => Promise<boolean>;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
  onDrop?: (e: React.DragEvent) => void;
}

const FolderRow: React.FC<FolderRowProps> = ({
  folder,
  depth,
  isActive,
  isExpanded,
  hasChildren = false,
  onSelect,
  onToggleExpand,
  onRename,
  onDelete,
  canEdit,
  canDelete,
  onDrop,
}) => {
  const [showRename, setShowRename] = useState(false);
  const [renameVal, setRenameVal] = useState(folder.name);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showConfirm, setShowConfirm] = useState(folder.file_count ? folder.file_count > 0 : false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setRenameVal(folder.name), [folder.name, folder.id]);
  useEffect(() => {
    if (showRename) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [showRename]);

  const handleRename = useCallback(async () => {
    if (!renameVal.trim() || renameVal === folder.name) {
      setShowRename(false);
      return;
    }
    const ok = await onRename(renameVal.trim());
    if (ok) setShowRename(false);
  }, [folder, renameVal, onRename]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    const ok = await onDelete();
    setIsDeleting(false);
    if (ok) setShowConfirm(false);
  }, [onDelete]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (onDrop) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        setIsDragOver(true);
      }
    },
    [onDrop],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDropEvent = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      onDrop?.(e);
    },
    [onDrop],
  );

  return (
    <>
      <ListItemButton
        dense
        onClick={onSelect}
        selected={isActive}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDropEvent}
        sx={{
          minHeight: ROW_HEIGHT,
          height: ROW_HEIGHT,
          pl: 2 + depth * 24,
          pr: 1,
          backgroundColor: isDragOver ? "#E3F2FD" : isActive ? "#DBE8F5" : "transparent",
          "&:hover": { bgcolor: "#ECF1F8" },
          "&.Mui-selected": { bgcolor: "#DBE8F5" },
          border: isDragOver ? "2px dashed #1976d2" : "2px solid transparent",
          transition: "background-color 0.15s ease",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Expand arrow */}
        {hasChildren && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            sx={{
              width: 22,
              height: 22,
              minWidth: 0,
              p: 0,
              mr: 0.5,
              transform: isExpanded ? "rotate(90deg)" : "rotate(0)",
              transition: "transform 0.15s ease",
              color: "#496682",
            }}
          >
            <ArrowForwardIosSharpIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ width: 22, mr: 1 }} />}

        <FolderIcon active={isActive} />

        {showRename ? (
          <TextField
            inputRef={inputRef}
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") setShowRename(false);
            }}
            size="small"
            autoFocus
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": { fontSize: 12, py: 0.25 },
              flex: 1,
              minWidth: 0,
              mr: 1,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <ListItemText
            primary={folder.name}
            primaryTypographyProps={{
              fontSize: 13,
              color: isActive ? "#0a3142" : "#496682",
              fontWeight: isActive ? 600 : 400,
              noWrap: true,
            }}
          />
        )}

        {folder.file_count ? folder.file_count > 0 && <FileCountChip count={folder.file_count} /> : null}

        {canEdit || canDelete ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            sx={{ color: "#92a7bc", ml: 0.5, px: 0.5 }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </ListItemButton>

      {/* Confirm-delete banner */}
      {showConfirm && (
        <Box
          sx={{
            pl: 2 + depth * 24 + 40,
            py: 0.5,
            bgcolor: "#FFF3E0",
            borderBottom: "1px solid #FFE0B2",
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Typography sx={{ fontSize: 11, color: "#E65100" }}>
              {`"${folder.name}" contains ${folder.file_count} file(s)`}
            </Typography>
            <Button size="small" disabled={isDeleting} onClick={handleDelete}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <Button size="small" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 1300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {canEdit && (
          <MenuItem
            onClick={() => {
              setShowRename(true);
              setAnchorEl(null);
            }}
          >
            Rename
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setShowConfirm(true);
              setAnchorEl(null);
            }}
            sx={{ color: "#d32f2f" }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/* === Main sidebar === */

export const ClientWorkspaceSidebar: React.FC<ClientWorkspaceSidebarProps> = ({
  clientId,
  activeFolderId,
  activeSmartView,
  onSelectFolder,
  onSelectSmartView,
  canUpload,
  canManageFolders,
  rootFolders,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onDropIntoFolder,
  onUploadClick,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Collect root folders and auto-expand active folder
  const [allFolders, setAllFolders] = useState<ClientWorkspaceFolder[]>([]);

  useEffect(() => {
    const roots = rootFolders || [];
    setAllFolders(roots);
    if (activeFolderId !== "all_files") {
      setExpandedIds((prev) => {
        const nid = Number(activeFolderId);
        return prev.has(nid) ? prev : new Set(prev).add(nid);
      });
    }
  }, [rootFolders, activeFolderId]);

  const toggleExpand = useCallback((folder: ClientWorkspaceFolder) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folder.id)) next.delete(folder.id);
      else next.add(folder.id);
      return next;
    });
  }, []);

  const getChildren = useCallback(
    (parentId: number | null): ClientWorkspaceFolder[] => {
      if (parentId === null) return [];
      return allFolders.filter((f) => f.parent_id === parentId);
    },
    [allFolders],
  );

  const renderTree = useCallback(
    (folders: ClientWorkspaceFolder[], depth: number) => {
      return folders.map((f) => {
        const children = getChildren(f.id);
        const isActive = activeFolderId === String(f.id);
        return (
          <React.Fragment key={f.id}>
            <FolderRow
              folder={f}
              depth={depth}
              isActive={isActive}
              isExpanded={expandedIds.has(f.id)}
              hasChildren={children.length > 0}
              onSelect={() => onSelectFolder(String(f.id))}
              onToggleExpand={() => toggleExpand(f)}
              onRename={async (name) => onRenameFolder(f.id, name)}
              onDelete={async () => {
                const ok = await onDeleteFolder(f.id);
                return ok;
              }}
              canEdit={canManageFolders}
              canDelete={canManageFolders}
              onDrop={onDropIntoFolder ? (e) => onDropIntoFolder(e, f) : undefined}
            />
            {expandedIds.has(f.id) && children.length > 0 && renderTree(children, depth + 1)}
          </React.Fragment>
        );
      });
    },
    [activeFolderId, expandedIds, getChildren, onDeleteFolder, onRenameFolder, onSelectFolder, toggleExpand, canManageFolders, onDropIntoFolder],
  );

  const totalFileCount = allFolders.reduce((sum, f) => sum + (f.file_count ?? 0), 0);

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        overflow: "auto",
        bgcolor: "transparent",
        borderRight: "1px solid #dae0e9",
        pr: 2,
      }}
    >
      {/* Action buttons */}
      <Stack sx={{ px: 2, py: 1.25, borderBottom: "1px solid #dae0e9" }} direction="column" gap={1}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: 12, color: "#0a3142", textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Workspace
        </Typography>

        <Stack direction="row" gap="10px">
          {canUpload && (
            <Button
              startIcon={<FileUploadIcon sx={{ fontSize: 16 }} />}
              onClick={onUploadClick || (() => toast.info("Upload dialog"))}
              variant="contained"
              data-testid="upload-button"
              sx={{
                height: "27px",
                fontSize: "14px",
                textTransform: "none",
                fontWeight: 400,
                bgcolor: "#071F35",
                color: "#fff",
                "&:hover": { bgcolor: "#0a3142" },
              }}
            >
              Upload
            </Button>
          )}
          {canManageFolders && (
            <Button
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                if (showNewFolderInput) {
                  setShowNewFolderInput(false);
                  setNewFolderName("");
                  return;
                }
                setShowNewFolderInput(true);
              }}
              variant="contained"
              data-testid="sidebar-new-folder"
              sx={{
                height: "27px",
                fontSize: "14px",
                textTransform: "none",
                fontWeight: 400,
                bgcolor: "#071F35",
                color: "#fff",
                "&:hover": { bgcolor: "#0a3142" },
              }}
            >
              {showNewFolderInput ? "Cancel" : "New Folder"}
            </Button>
          )}
        </Stack>

        {showNewFolderInput && (
          <TextField
            size="small"
            autoFocus
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && newFolderName.trim()) {
                setIsCreating(true);
                try {
                  await onCreateFolder(null, newFolderName.trim());
                  setNewFolderName("");
                  setShowNewFolderInput(false);
                } catch {
                  // Keep input open so the user can retry
                } finally {
                  setIsCreating(false);
                }
              }
              if (e.key === "Escape") {
                setShowNewFolderInput(false);
                setNewFolderName("");
              }
            }}
            onBlur={async () => {
              if (newFolderName.trim()) {
                setIsCreating(true);
                await onCreateFolder(null, newFolderName.trim());
                setNewFolderName("");
                setShowNewFolderInput(false);
                setIsCreating(false);
              } else {
                setShowNewFolderInput(false);
              }
            }}
            disabled={isCreating}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 12, py: 0.4 } }}
          />
        )}
      </Stack>

      {/* Smart Views */}
      <Box data-testid="sidebar-smart-views" sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            color: "#8b9eb0",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            px: 2,
            mb: 0.5,
          }}
        >
          Views
        </Typography>
        {SMART_VIEWS.map((view) => (
          <SmartViewRow
            key={view.id}
            view={view}
            isActive={activeSmartView === view.id || (activeFolderId === "all_files" && view.id === "all_files")}
            onSelect={() => onSelectSmartView?.(view.id)}
          />
        ))}
      </Box>

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Folders section */}
      <Box data-testid="sidebar-folders">
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            color: "#8b9eb0",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            px: 2,
            mb: 0.5,
          }}
        >
          Folders
        </Typography>
        {renderTree(allFolders, 0)}
        {allFolders.length === 0 && (
          <Typography
            sx={{
              px: 2,
              py: 2,
              fontSize: 12,
              color: "#8b9eb0",
              textAlign: "center",
            }}
          >
            No folders yet
          </Typography>
        )}
      </Box>
    </Box>
  );
};
